// const reactdndpreview = require("react-dnd-preview");
import * as moment from "moment";
import { IKeyValue, resourceNameKeyValues } from "motw_core/Entity/IKeyValue";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { GetEnumElementFromString } from "motw_core/Utils/EnumUtils";
import * as React from "react";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import { Outlet } from "react-router-dom";
import { IsMobileDevice } from "source/app/domain/Device/Helpers/IsMobile";
import Login from "source/app/presentation/Login/Login";
import { ReactDndPreview } from "source/app/presentation/Preview/ReactDndPreview";
import { RouterNavigationTarget } from "source/app/presentation/Routing/RouterNavigationTarget";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import Device from "../domain/Device/Device";
import Player from "../domain/Player";
import Data from "../services/Data";
import MotwLogicServer from "../services/External/LogicServer/MotwLogicServer";
import { default as NameOf, default as Nameof } from "../services/NameOf";
import IsNotEqual from "../services/Shared/ReactHelpers";
import { ESharedComponent } from "../services/UI/State/ESharedComponent";
import { CanSetState, SetState } from "../services/UI/State/SetState";
import { ShareState } from "../services/UI/State/ShareState";
import { State } from "../services/UI/State/State";
import * as styles from "./AppContainer.css";
import { BottomBar } from "./BottomBar/BottomBar";
import Header from "./Header/Header";
import { KeyValueLogs } from "./MainPanel/KeyValueLogs/KeyValueLogs";
import { LogInputParticles } from "./MainPanel/Log/LogInputForm/LogInputParticles/LogInputParticles";
import { NatureBackgrounds } from "./NatureBackgrounds/NatureBackgrounds";
import { ScrollPositionTracker } from "./ScrollPositionTracker";
import { DisplayTooltipsContainer } from "./Shared/DisplayTooltipsContainer/DisplayTooltipsContainer";
import { InCenterOfScreen } from "./Shared/InCenterOfScreen/InCenterOfScreen";
import { FontPreferenceController } from "./Shared/Fonts/FontPreferenceController";
import Render from "./Shared/Render";
import { Style, StyleClass } from "./Shared/Styles/CSS";
import { Stars } from "./Stars/Stars";
import { EEventType } from "../services/Events/EEventType";
import { DispatchEvent } from "../services/Events/Events";

export interface IAppContainerState {
    level: number;
    keyValues: IKeyValue[];
    quickJournalIsOpen: boolean;
}

export default class AppContainer extends React.Component<
    {},
    IAppContainerState
> {
    constructor(props: any) {
        super(props);

        this.state = {
            level: 0,
            keyValues: [],
            quickJournalIsOpen: false,
        };
    }

    async componentDidMount() {
        State.ShareClass(ESharedComponent.AppContainer, this);

        if (Player.IsSignedIn) {
            Data.Load.Component({
                component: this,
                load: MotwLogicServer.GetPlayerLevelCurrent,
                stateTarget: NameOf<IAppContainerState>("level"),
                inLoadAll: true,
            });

            LoadKeyValues(this);
            Device.Initialize();
        }

        // DispatchEvent(EEventType.AppContainer_DataChanged);
    }

    async componentDidUpdate(prevProps: {}, prevState: IAppContainerState) {
        State.ShareClass(ESharedComponent.AppContainer, this);

        if (IsNotEqual(prevState.keyValues, this.state.keyValues)) {
            SetAllKeyValueComponentStatesAsync(this.state.keyValues);
        }
    }

    render() {
        if (!Player.IsSignedIn) {
            return <Login />;
        }

        const diversityTarget = GetDiversityTargetValue(this.state.keyValues);

        return (
            <div id="pnlHome" className={styles.pnlHome}>
                <RouterNavigationTarget />
                <FontPreferenceController />
                <DndProvider
                    backend={TouchBackend}
                    options={{ enableMouseEvents: true, delayTouchStart: 200 }}
                    debugMode={true}
                >
                    <Header
                        level={this.state.level}
                        diversityTarget={diversityTarget}
                        hidden={this.state.quickJournalIsOpen}
                    />
                    <Render
                        id="mobileBackgroundItems"
                        when={IsMobileDevice()}
                        withClasses={StyleClass(`backgroundsFixed`, {
                            position: `fixed`,
                            top: 0,
                            left: 0,
                        })}
                    >
                        <Stars />
                        <NatureBackgrounds />
                        {/* <Sunrise /> */}
                    </Render>
                    <Render
                        id="desktopBackgroundItems"
                        when={!IsMobileDevice()}
                        withClasses={StyleClass(`backgroundsFixed`, {
                            position: `fixed`,
                            top: 0,
                            left: 0,
                        })}
                    >
                        <LogInputParticles />
                    </Render>
                    <div
                        id="calcTarget_appBody"
                        {...Style(`appBodySpacing`, {
                            marginTop: `75px`,
                            transition: `margin-top 180ms ease`,
                        })}
                    >
                        <ScrollPositionTracker />
                        <KeyValueLogs hidden={this.state.quickJournalIsOpen} />
                        <SystemDateWarning />
                        <Outlet />
                        <BottomBar
                            onDisplayLargeChange={(displayLarge) =>
                                this.setState({
                                    quickJournalIsOpen: displayLarge,
                                })
                            }
                        />
                        <InCenterOfScreen />
                        <DisplayTooltipsContainer />
                    </div>
                    <ReactDndPreview />
                </DndProvider>
            </div>
        );
    }
}

async function SetAllKeyValueComponentStatesAsync(keyValues: IKeyValue[]) {
    keyValues.forEach(async (kv) => {
        const sharedStateKey = GetEnumElementFromString(
            ESharedFunction,
            kv.key
        )!;

        if (sharedStateKey !== undefined && CanSetState(sharedStateKey)) {
            SetState<IKeyValue>(sharedStateKey, kv);
        }
    });

    SetState<string>(
        ESharedFunction.BroadStateSelectors_keyValueUpdateTime,
        new Date().toString()
    );
}

function GetDiversityTargetValue(keyValues: IKeyValue[]) {
    const defaultDiversityTarget = 99;

    if (keyValues.length === 0) {
        return defaultDiversityTarget;
    }

    const diversityKeyValue = keyValues.find(
        (kv) => kv.key === ESharedFunction[ESharedFunction.Diversity_target]
    );

    if (diversityKeyValue === undefined) {
        return defaultDiversityTarget;
    }

    return Number.parseFloat(diversityKeyValue.value);
}

function LoadKeyValues(appContainer: AppContainer) {
    Data.Load.Component({
        component: appContainer,
        load: () =>
            ClientDataProvider.GetRecordsAsync<IKeyValue>(
                resourceNameKeyValues
            ),
        stateTarget: Nameof<IAppContainerState>("keyValues"),
        inLoadAll: true,
    });
}

function SystemDateWarning() {
    const [offset, setOffset] = React.useState<number>(0);

    ShareState(ESharedFunction.SystemDateWarning_offset, offset, setOffset);

    if (offset === 0) {
        return null;
    }

    const styleClass = StyleClass(`systemDateWarningContainer`, {
        textAlign: "center",
        padding: "10px",
        background: "hsla(0, 100%, 50%, 0.2)",
        borderRadius: "3px",
        margin: "0px ​30p",
    });

    const systemDateText = moment(new Date())
        .add(offset, "days")
        .format("dddd, MMMM Do YYYY");

    return (
        <div className={styleClass}>
            Current system date has been set to {systemDateText}
        </div>
    );
}
